import type { LitElement, ReactiveController } from "lit";

export type ValidatorFunction<T> = (value: T) => string | undefined | Promise<string | undefined>;

export interface FormValidationOptions<TData = object> {
  alwaysValidateOnInput?: boolean;
  validateOnInputWhenDirty?: boolean;
  validateOnBlur?: boolean;
  validators?: {
    [K in keyof TData]?: ValidatorFunction<TData[K]>[];
  };
}

/**
 * Self-contained reactive controller that manages all form functionality:
 * data management, validation, DOM interactions, and lifecycle.
 * No external dependencies - everything is handled internally.
 *
 * @param host - The LitElement host component that this controller is attached to.
 * @param initialData - Initial data object that defines the form structure and default values.
 * @param options - Optional validation options including field-specific validators.
 * @param onSubmit - Callback function to handle form submission, receives form data and validity status.
 * @param formSelector - querySelector to find the form element within the component's render root
 */
export class WowFormController<TData extends object> implements ReactiveController {
  private isInitialized = false;
  private formElement: HTMLFormElement | null = null;
  private validators = new Map<string, ValidatorFunction<unknown>[]>();
  private initialData: TData;
  private options: FormValidationOptions<TData>;

  readonly fieldNames: { readonly [K in keyof TData]: K };
  initErrorMessage: string | undefined;
  initialDormData?: FormData;
  constructor(
    private host: LitElement,
    initialData: TData,
    options: FormValidationOptions<TData> = {},
    protected onSubmit: (ev: { data?: TData; formData?: FormData; isValid: boolean }) => void = () => {},
    private formSelector: string = "form",
  ) {
    this.host.addController(this);
    this.initialData = initialData;
    this.options = {
      alwaysValidateOnInput: false,
      validateOnBlur: true,
      validateOnInputWhenDirty: true,
      ...options,
    };

    // Create type-safe field names proxy
    this.fieldNames = new Proxy({} as { readonly [K in keyof TData]: K }, {
      get: (_, prop) => prop,
    });

    // Initialize validators from options
    if (options.validators) {
      Object.entries(options.validators).forEach(([fieldName, validatorArray]) => {
        if (validatorArray) {
          this.validators.set(fieldName, [...(validatorArray as ValidatorFunction<unknown>[])]);
        }
      });
    }
  }

  hostConnected(): void {
    // Reset state when component connects
    this.isInitialized = false;
    this.formElement = null;
  }

  hostUpdated(): void {
    // Initialize form binding after render is complete
    if (!this.isInitialized) {
      this.initializeForm();
    }
  }

  hostDisconnected(): void {
    // Clean up form binding
    this.cleanup();
    this.validators.clear();
  }

  private initializeForm(): void {
    try {
      const renderRoot = this.host.renderRoot;
      const formElement = renderRoot.querySelector(this.formSelector) as HTMLFormElement;

      if (!formElement) {
        this.initErrorMessage = `WowFormController: No form element found with selector "${this.formSelector}". Ensure your component template contains a <form> element.`;
        return;
      }

      if (formElement.tagName !== "FORM") {
        this.initErrorMessage = `WowFormController: Element found with selector "${this.formSelector}" is not a form element. Found: ${formElement.tagName}`;
        return;
      }

      this.formElement = formElement;

      this.initializeFormFields();
      this.setupEventListeners();

      this.isInitialized = true;
      this.initErrorMessage = undefined;
      this.initialDormData = new FormData(this.formElement);
    } catch (error) {
      console.error("WowFormController initialization failed:", error);
      throw error;
    }
  }

  private initializeFormFields(): void {
    if (!this.formElement) return;

    Object.entries(this.initialData).forEach(([name, value]) => {
      const element = this.formElement?.elements.namedItem(name);
      if (element && value != undefined) {
        // Only set if element exists in form
        this.setValue(name as keyof TData, value);
      }
    });
  }
  //validateOnInputAfterFirstErrorDetection
  private setupEventListeners(): void {
    if (!this.formElement) return;

    for (let i = 0; i < this.formElement.elements.length; i++) {
      const el = this.formElement.elements.item(i);

      el?.addEventListener("focusout", this.handleBlur.bind(this));

      el?.addEventListener("input", this.handleInput.bind(this));
    }

    // Handle form submission
    this.formElement.addEventListener("submit", this.handleSubmit.bind(this));

    // Handle form keydown
    this.formElement.addEventListener("keydown", this.handleKeydown.bind(this));
  }

  private async handleInput(event: Event): Promise<void> {
    const target = event.target as HTMLInputElement;

    if (
      target &&
      target.name &&
      this.validators.has(target.name) &&
      (this.options.alwaysValidateOnInput ||
        (this.options.validateOnInputWhenDirty &&
          (!target.validity.valid || this.getInitialValue(target.name as keyof TData) !== target.value)))
    ) {
      await this.validateField(target.name as keyof TData);
    }
  }

  private async handleBlur(event: Event): Promise<void> {
    const target = event.target as HTMLInputElement;

    if (target.name && this.validators.has(target.name) && this.options.validateOnBlur) {
      await this.validateField(target.name as keyof TData);
    }
  }

  private async handleSubmit(event: Event): Promise<void> {
    event.preventDefault();

    if (!this.formElement) {
      if (this.initErrorMessage) {
        throw new Error(this.initErrorMessage);
      }
      return;
    }

    try {
      // Validate all fields before submission
      const isValid = await this.validateAll();

      if (!isValid) {
        // Show native validation UI for first invalid field
        this.reportValidity();
        return;
      }

      const eventInit = {
        detail: {
          data: this.getData(),
          formData: this.getFormData(),
          isValid,
        },
        cancelable: true,
      };

      // Dispatch custom submit event with form data

      this.onSubmit(eventInit.detail);

      this.initialData = eventInit.detail.data as TData;

      const submitEvent = new CustomEvent("wow-submit", eventInit);
      if ("dispatchEvent" in this.host && typeof this.host.dispatchEvent === "function") {
        this.host.dispatchEvent(submitEvent);
      }
    } catch (error) {
      console.error("Form submission error:", error);
    }
  }

  private async handleKeydown(event: KeyboardEvent): Promise<void> {
    if (event.code === "Enter") {
      this.submit();
    }
  }

  private cleanup(): void {
    if (this.formElement) {
      // Remove event listeners
      this.formElement.removeEventListener("input", this.handleInput.bind(this));
      this.formElement.removeEventListener("focusout", this.handleBlur.bind(this));
      this.formElement.removeEventListener("submit", this.handleSubmit.bind(this));
      this.formElement.removeEventListener("keydown", this.handleKeydown.bind(this));
    }

    this.isInitialized = false;
    this.formElement = null;
  }

  /**
   * Set a field value using native form APIs
   */
  setValue(name: keyof TData, value: FormDataEntryValue | unknown): void {
    if (!this.formElement) return;

    // Only set value if this field is part of our form data interface
    if (!(name in this.initialData)) return;

    const element = this.formElement.elements.namedItem(name.toString()) as
      | HTMLInputElement
      | HTMLSelectElement
      | HTMLTextAreaElement;
    if (!element) return;

    if ("value" in element) {
      element.value = String(value);
    }
    if ("checked" in element && typeof value === "boolean") {
      (element as HTMLInputElement).checked = value;
    }
    // Trigger native events for proper form behavior
    element.dispatchEvent(new Event("input", { bubbles: true }));
    element.dispatchEvent(new Event("change", { bubbles: true }));
  }

  /**
   * Get a field value using native FormData API
   */
  getValue<K extends keyof TData>(name: K): TData[K] | undefined {
    if (!this.formElement) return undefined;

    const formData = new FormData(this.formElement);
    return formData.get(name as string) as TData[K] | undefined;
  }

  /**
   * Get a field value using native FormData API
   */
  getInitialValue<K extends keyof TData>(name: K): TData[K] | undefined {
    if (!this.formElement || !this.initialDormData) return undefined;

    return this.initialDormData.get(name as string) as TData[K] | undefined;
  }

  /**
   * Get all form values as FormData (native browser serialization)
   */
  getFormData(): FormData | undefined {
    if (!this.formElement) return;
    return new FormData(this.formElement);
  }

  /**
   * Get form data as a plain object
   */
  getData(): TData | undefined {
    if (!this.formElement) return;

    const formData = this.getFormData();
    if (!formData) return;

    const data: TData = { ...this.initialData };

    // Only process fields that exist in our interface
    for (const [key, value] of formData.entries()) {
      if (key in this.initialData) {
        data[key as keyof TData] = value as TData[keyof TData];
      }
    }

    return data;
  }

  /**
   * Validate a specific field using native setCustomValidity
   */
  async validateField(name: keyof TData): Promise<boolean> {
    if (!this.formElement) return false;

    const element = this.formElement.elements.namedItem(name as string) as
      | HTMLInputElement
      | HTMLSelectElement
      | HTMLTextAreaElement;
    const validators = this.validators.get(name as string);

    if (!element || !validators || validators.length === 0) {
      return true;
    }

    try {
      const value = this.getValue(name);

      // Run all validators and find first error
      for (const validator of validators) {
        const result = await Promise.resolve(validator(value));
        if (result) {
          element.setCustomValidity(result);
          return false;
        }
      }

      // No errors found
      element.setCustomValidity("");
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Validation error";
      element.setCustomValidity(message);
      return false;
    }
  }

  /**
   * Validate all fields with validators
   */
  async validateAll(): Promise<boolean> {
    if (!this.formElement) return false;

    const validationPromises = Array.from(this.validators.keys()).map((name) =>
      this.validateField(name as keyof TData),
    );

    const results = await Promise.all(validationPromises);
    return results.every((result) => result);
  }

  /**
   * Check form validity using native browser API
   */
  checkValidity(): boolean {
    if (!this.formElement) return false;
    return this.formElement.checkValidity();
  }

  /**
   * Report validity using native browser validation UI
   */
  reportValidity(): boolean {
    if (!this.formElement) return false;
    return this.formElement.reportValidity();
  }

  /**
   * Reset form using native API and optionally set new values
   */
  reset(data?: Partial<TData>): void {
    if (!this.formElement) return;

    this.formElement.reset();

    const resetData = data ? { ...this.initialData, ...data } : this.initialData;
    this.initialData = resetData as TData;
    Object.entries(resetData).forEach(([name, value]) => {
      this.setValue(name as keyof TData, value);
    });

    // Clear any custom validation messages
    this.validators.forEach((_, name) => {
      const element = this.formElement?.elements.namedItem(name as string) as HTMLInputElement;
      if (element) {
        element.setCustomValidity("");
      }
    });
  }

  /**
   * Check if form has unsaved changes by comparing current data with initial data
   */
  isDirty(): boolean {
    if (!this.isInitialized || !this.formElement) {
      return false;
    }
    const currentData = this.getData();
    if (!currentData) return false;

    // Compare current data with initial data
    for (const key in this.initialData) {
      if (String(this.initialData[key as keyof TData]) !== String(currentData[key as keyof TData])) {
        return true;
      }
    }

    // Check for new fields in current data
    for (const key in currentData) {
      if (!(key in this.initialData)) {
        const initialValue = this.initialData[key as keyof TData];
        if (initialValue === undefined || initialValue === null) {
          if (currentData[key as keyof TData] !== initialValue) return true;
        }
      }
    }
    return false;
  }

  /**
   * Programmatically submit the form
   */
  async submit(): Promise<boolean> {
    if (!this.formElement) {
      if (this.initErrorMessage) {
        console.error(this.initErrorMessage);
      }
      return false;
    }

    const isValid = await this.validateAll();
    if (!isValid) {
      this.reportValidity();
      return false;
    }

    const eventDetail = { data: this.getData(), formData: this.getFormData(), isValid: true };
    this.onSubmit(eventDetail);
    return true;
  }
}
